import { api } from '@/api/client'
import type { Exercise, ExerciseInput } from '@/pages/workouts/api'

export type Recognition = {
  recognized: boolean
  summary: string
  manufacturer: string | null
  model: string | null
  draft: ExerciseInput | null
  matches: { exercise: Exercise; relation: 'exact' | 'similar'; reason: string }[]
}

export function recognizeExercise(text: string, photo: File | null, signal: AbortSignal): Promise<Recognition> {
  const data = new FormData()
  data.set('text', text.trim())
  if (photo) data.set('photo', photo)
  return api.post<Recognition>('/exercise-recognition', data, { signal })
}

/** Resize before upload and omit camera metadata. The original stays on the device. */
export async function prepareRecognitionPhoto(file: File): Promise<File> {
  if (file.size > 25 * 1024 * 1024) throw new Error('Фото слишком большое — выбери файл до 25 МБ')
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error('Не удалось открыть фото — выбери JPEG, PNG или WebP'))
      image.src = url
    })
    const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Не удалось подготовить фото — попробуй другой браузер')
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((value) => value ? resolve(value) : reject(new Error('Не удалось подготовить фото')), 'image/jpeg', 0.85)
    })
    return new File([blob], 'machine.jpg', { type: 'image/jpeg' })
  } finally {
    URL.revokeObjectURL(url)
  }
}
